import { AppDispatch, RootState } from "../redux/store"
import { LoadStatus, setDocStatus } from "../redux/slices/docMeta"
import { localStore } from "../storage/storage"
import { DocData } from "./types"
import { getResource } from "../datastore"
import { DocInfo } from "../datastore/models"
import log from "loglevel"
import { getDocLoadStatus } from "./getDocLoadStatus"
import { requestAndStoreDoc } from "./requestAndStoreDoc"

export interface GetDocResult extends DocData {
  isError: boolean
}

export const getDoc =
  (id: number) =>
  async (dispatch: AppDispatch, getState: () => RootState, services: any) => {
    const state = getState()

    // Default to an empty blob
    let result: GetDocResult = {
      docBlob: new Blob([]),
      docExt: "",
      isError: true
    }

    const docInfo = getResource<DocInfo>(state.resources.docInfo, id)
    if (!docInfo) {
      log.debug("getDoc unable to get docInfo record ", id)
      return result
    }

    const loadStatus = getDocLoadStatus(
      state.resources.docInfo,
      state.docMeta.docStatus,
      docInfo.id
    )
    if (loadStatus === null) {
      log.error("getDoc unable to get doc load status ", docInfo.id)
      return result
    }

    if (
      loadStatus === LoadStatus.COMPLETE ||
      loadStatus === LoadStatus.SAVE_PENDING ||
      loadStatus === LoadStatus.SAVING
    ) {
      // If document is still in the process of being saved to backend, it may be stored locally under either
      // 'real' id or localId
      // -> determine which based on what we find docStatus under
      const localStoreId = state.docMeta.docStatus[docInfo.id]
        ? docInfo.id
        : docInfo.localId

      const docBlob = await localStore.get(`docs/${localStoreId}`)
      if (docBlob !== null) {
        result = {
          docBlob: docBlob,
          docExt: docInfo.fileName.split(".").pop() as string,
          isError: false
        }

        // Determine localStoreId again just in case it moved while awaiting localStore.get
        const localStoreId = getState().docMeta.docStatus[docInfo.id]
          ? docInfo.id
          : docInfo.localId
        if (localStoreId) {
          // Update status 'lastUsed' date
          dispatch(
            setDocStatus({
              docId: localStoreId,
              docStatus: {
                lastUsed: new Date().toISOString()
              }
            })
          )
        }
      } else {
        log.warn(
          "getDoc unable to load file from local storage: ",
          docInfo.fileName
        )

        // If we failed to get the file from local storage, request it directly from server
        // This provides a fallback if:
        // - file was previously written to local storage but somehow erased
        // - local storage isn't available
        const reqDocResult = await requestAndStoreDoc(
          dispatch,
          docInfo,
          services
        )        
        if (reqDocResult.loadStatus === LoadStatus.COMPLETE) {
          result = {
            docBlob: reqDocResult.docData,
            docExt: docInfo.fileName.split(".").pop() as string,
            isError: false
          }

        } else {
            log.warn(
                "getDoc unable to get file from server: ",
                docInfo.fileName
              )
        }
      }
    }
    return result

  }
